import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ConnectionStatus } from '@capacitor/network';
import { isNil } from 'lodash';

import { getCurrentGuestSession, getCurrentSession } from 'remote/auth';
import { QUERY_TYPE, getGraphqlApiUrl } from 'remote/constants';
import { NetworkConnectionError } from 'remote/exceptions';
import { getSanityGraphqlQueryUrl, getSanityGroqQueryUrl } from 'remote/index';
import { useCdpContext } from 'state/cdp';
import { useErrorContext } from 'state/errors';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { sanityDataset as defaultSanityDataset, isProduction } from 'utils/environment';
import * as location from 'utils/location';
import { logger } from 'utils/logger';
import { FetchConfig, fetch } from 'utils/network';
import { Network } from 'utils/network/status';

export interface QueryConfig<R, V, C = never> extends Omit<FetchConfig<R, V, C>, 'language'> {
  queryType: QUERY_TYPE;
}

export type Query = <R = any, V = any, C = never>(o: QueryConfig<R, V, C>) => Promise<R | C>;

const networkLogger = logger.child({});

/*
 * type Config = {
 *   queryType: queryType,
 *   query: string,
 *   variables?: Object,
 *   headers?: Object,
 *   then?: (data: Object) => any,
 *   catch?: (error: Error) => any,
 * }
 */

export const useNetwork = () => {
  const { language, region } = useLocale();

  const { sessionId } = useCdpContext();
  const { handleError } = useErrorContext();

  const [connection, setConnection] = useState<ConnectionStatus>({
    // assume user is connected to the internet on app mount.
    connected: true,
    connectionType: 'unknown',
  });

  networkLogger.setContext({ networkConnectionStatus: connection });

  const [hasNetworkError, setHasNetworkError] = useState(false);
  const [hasNotAuthenticatedError, setHasNotAuthenticatedError] = useState(false);
  const useSanityCdn = useFlag(LaunchDarklyFlag.ENABLE_SANITY_CDN);

  const sanityEndpoints = useMemo(() => {
    // use cdn by default if LD flag is null or undefined
    const useCdn = isNil(useSanityCdn) ? true : useSanityCdn;

    const sanityDataset = region
      ? `${defaultSanityDataset}_${region.toLowerCase()}`
      : defaultSanityDataset.toLowerCase();

    const groq = getSanityGroqQueryUrl({ sanityDataset, useCdn });
    const graphql = getSanityGraphqlQueryUrl({ sanityDataset, useCdn });

    return { graphql, groq };
  }, [region, useSanityCdn]);

  useEffect(() => {
    // Get the current network status
    Network.getStatus()
      .then(status => {
        setConnection(status);
        setHasNetworkError(!status.connected);
      })
      .catch(() => {
        // should we assume user is not connected if
        // we are unable to get network status?
        setConnection({
          connected: false,
          connectionType: 'none',
        });
        setHasNetworkError(true);
      });

    const networkListenerPromise = Network.addListener('networkStatusChange', status => {
      // If the network is coming back, refresh the page since it's creating issues without it
      if (hasNetworkError && status.connected) {
        location.reload();
      }

      setConnection(status);
      // When the user has no internet, show the network error page and when internet is back up, show the content
      setHasNetworkError(!status.connected);
    });

    return () => {
      networkListenerPromise.then(handler => handler.remove());
    };
  }, [hasNetworkError]);

  const getEndpointUrl = useCallback(
    (queryType: QUERY_TYPE) => {
      switch (queryType) {
        case QUERY_TYPE.LambdaGraphqlQuery:
          return getGraphqlApiUrl();
        case QUERY_TYPE.SanityGraphqlQuery:
          return sanityEndpoints.graphql;
        case QUERY_TYPE.SanityGroqQuery:
          return sanityEndpoints.groq;
        default:
          throw new Error('Unknown Query Type');
      }
    },
    [sanityEndpoints]
  );

  const getCurrentGuestToken = useCallback(() => {
    const guestSession = getCurrentGuestSession();
    return guestSession ? guestSession.getIdToken().getJwtToken() : null;
  }, []);

  const query = useCallback<Query>(
    ({ queryType, ...config }) => {
      return getCurrentSession().then(userSession => {
        let token = userSession ? userSession.getIdToken().getJwtToken() : getCurrentGuestToken();
        const headers: { [key: string]: string } = {
          'Content-Type': 'application/json',
          ...config.headers,
        };

        const endpoint = getEndpointUrl(queryType);

        if (queryType === QUERY_TYPE.LambdaGraphqlQuery && token) {
          headers.Authorization = `Bearer ${token}`;
        }

        return fetch(endpoint, {
          ...config,
          connection,
          headers,
          language,
          region,
          sessionId,
        }).catch((err: Error | NetworkConnectionError) => {
          const jwtExpiredError = 'JWT is invalid or expired';
          if (err.message.includes(jwtExpiredError)) {
            setHasNotAuthenticatedError(true);
          }

          if (Object.prototype.hasOwnProperty.call(err, 'connectionError')) {
            networkLogger.error({ error: err, message: 'Network connection error' });
            setHasNetworkError(true);
            throw err;
          }
          handleError(err);
          throw err;
        });
      });
    },
    [getCurrentGuestToken, getEndpointUrl, connection, language, region, sessionId, handleError]
  );

  const defaultErrorHandler = useCallback(
    (error: Error) => {
      // We already have network issues. Bail out
      if (hasNetworkError) {
        return;
      }

      const message = isProduction ? 'Network Error - Default Error Handler' : error.message;
      networkLogger.error({ error, message });
    },
    [hasNetworkError]
  );

  const ctx = useMemo(
    () => ({
      connection,
      defaultErrorHandler,
      hasNetworkError,
      hasNotAuthenticatedError,
      query,
      sanityEndpoints,
      setHasNetworkError,
      setHasNotAuthenticatedError,
    }),
    [
      query,
      connection,
      hasNetworkError,
      defaultErrorHandler,
      sanityEndpoints,
      hasNotAuthenticatedError,
      setHasNotAuthenticatedError,
    ]
  );

  return ctx;
};
